import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";
import { Button } from "components/common/Button";
import { getFormDefaultValues } from "./utils";
import { ElementNote } from "./ElementNote";
import { GroupVisibilityToggleButton } from "../GroupVisibilityToggleButton";
import type { GroupedSmartSurveyorElement } from "components/SmartSurveyor/ReviewPage/types";
import type { SelectOption } from "components/common/Select/types";
import type { GroupSelectionV3Form } from "../hooks/useGroupSelectionV3/useGroupSelectionV3";

interface ElementDetailsV3Props {
  canEdit: boolean;
  form: GroupSelectionV3Form;
  element: GroupedSmartSurveyorElement;
  groupTypeOptions: SelectOption[];
  isEditingElement: boolean;
  onToggleEditMode: () => void;
  onDelete?: () => void;
  isElementHidden: boolean;
  onToggleElementVisibility: () => void;
  /** Optional `data-onboarding` attribute value for product-tour targeting. */
  dataOnboarding?: string;
}

export const ElementDetailsV3 = memo(
  ({
    canEdit,
    form,
    element,
    groupTypeOptions,
    isEditingElement,
    onToggleEditMode,
    onDelete,
    isElementHidden,
    onToggleElementVisibility,
    dataOnboarding,
  }: ElementDetailsV3Props) => {
    const { t } = useTranslation();

    const handleSave = useCallback(() => {
      form.handleSubmit();
    }, [form]);

    const handleCancel = useCallback(() => {
      form.reset(getFormDefaultValues(element));
    }, [form, element]);

    return (
      <form.AppForm>
        <div data-onboarding={dataOnboarding} className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto pt-1 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("smartSurveyor.elementDetails.form.elementDetails")}
              </div>
              <div className="flex items-center gap-2">
                <GroupVisibilityToggleButton
                  isHidden={isElementHidden}
                  onToggle={onToggleElementVisibility}
                  title={
                    isElementHidden
                      ? t("smartSurveyor.reviewPage.showElement")
                      : t("smartSurveyor.reviewPage.hideElement")
                  }
                />
                {canEdit && (
                  <Button
                    variant={isEditingElement ? "primary" : "outline"}
                    size="sm"
                    onClick={onToggleEditMode}
                    startAdornment={<MapPin size={14} />}
                  >
                    {isEditingElement
                      ? t("smartSurveyor.elementDetails.form.editModeOn")
                      : t("smartSurveyor.elementDetails.form.editMode")}
                  </Button>
                )}
              </div>
            </div>
            <div className="pt-1">
              <form.AppField name="multiplier">
                {(field) => (
                  <field.NumberField
                    label={t("smartSurveyor.elementDetails.form.multiplier")}
                    isEditable={canEdit}
                    step={1}
                    min={1}
                    inputMode="numeric"
                  />
                )}
              </form.AppField>
            </div>
            <div className="pt-1">
              <form.AppField name="group_id">
                {(field) => (
                  <field.SelectField
                    label={t("smartSurveyor.elementDetails.form.elementType")}
                    isEditable={canEdit}
                    options={groupTypeOptions}
                  />
                )}
              </form.AppField>
            </div>
            <ElementNote canEdit={canEdit} element={element} form={form} />
          </div>
          {canEdit && (
            <div className="shrink-0 pt-3">
              <form.HeaderActions
                onCancel={handleCancel}
                onSave={handleSave}
                onDelete={onDelete}
              />
            </div>
          )}
        </div>
      </form.AppForm>
    );
  },
);
